import React, {useEffect, useState} from "react";
import ModalV2 from "@/components/portal/ModalV2";
import {platformAPI} from "../../../api/axiosConfig";
import {Contact} from "../typedefs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncIcon from "@mui/icons-material/Sync";

type PhoneResult = {
    phone: string;
    listed: boolean;
    error?: string;
};

type AccountResult = {
    accountSID: string;
    phones: PhoneResult[];
};

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    contact: Contact;
    clientID: string | null;
};

export function SafelistCheckModal({open, setOpen, contact, clientID}: Props) {
    const [results, setResults] = useState<AccountResult[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [synced, setSynced] = useState(false);

    useEffect(() => {
        if (!open) return;
        setResults(null);
        setError(null);
        setSynced(false);
        setLoading(true);
        platformAPI.get<AccountResult[]>("api/contacts/safelist-check", {
            params: {contactID: contact.id, ...(clientID ? {clientID} : {})},
        })
            .then(res => setResults(res.data))
            .catch(err => setError(err?.response?.data?.error ?? "Failed to check safelist"))
            .finally(() => setLoading(false));
    }, [open, contact.id, clientID]);

    const hasUnlisted = results !== null && results.some(a => a.phones.some(p => !p.listed && !p.error));

    const queueSync = () => {
        setSyncing(true);
        platformAPI.post(`api/contacts/${contact.id}/queue-safelist-sync`)
            .then(() => setSynced(true))
            .catch(err => setError(err?.response?.data?.error ?? "Failed to queue sync"))
            .finally(() => setSyncing(false));
    };

    const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.id;

    return (
        <ModalV2 open={open} setOpen={setOpen} contentClassName="w-[560px] max-w-[95vw]">
            <div className="flex flex-col gap-1 pb-3 border-b border-light-light-grey">
                <h2 className="text-base font-semibold pr-8 leading-tight">Twilio Safe List</h2>
                <p className="text-sm text-light-grey">{name}</p>
            </div>

            {loading && (
                <div className="flex items-center gap-2 py-4 text-sm text-light-grey">
                    <span className="inline-block w-4 h-4 border-2 border-steel border-t-grey rounded-full animate-spin"/>
                    Checking safe list…
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 py-3 px-3 bg-light-red rounded-md text-sm text-dark-red border border-mid-red">
                    <ErrorOutlineIcon fontSize="small"/>
                    {error}
                </div>
            )}

            {results !== null && results.length === 0 && (
                <p className="text-sm text-light-grey py-2">No Twilio drivers found for this client.</p>
            )}

            {results !== null && results.length > 0 && (
                <div className="flex flex-col gap-3">
                    {results.map(acct => (
                        <div key={acct.accountSID} className="rounded-lg border border-light-light-grey overflow-hidden">
                            <div className="bg-desaturated-grey px-3 py-2 border-b border-light-light-grey">
                                <span className="text-xs font-mono text-grey select-all">{acct.accountSID}</span>
                            </div>
                            <div className="divide-y divide-light-light-grey">
                                {acct.phones.map(p => (
                                    <div key={p.phone} className="flex items-center gap-3 px-3 py-2.5">
                                        {p.error ? (
                                            <ErrorOutlineIcon fontSize="small" className="text-yellow shrink-0"/>
                                        ) : p.listed ? (
                                            <CheckCircleIcon fontSize="small" className="text-green shrink-0"/>
                                        ) : (
                                            <CancelIcon fontSize="small" className="text-red shrink-0"/>
                                        )}
                                        <span className="font-mono text-sm flex-1">{p.phone}</span>
                                        {p.error ? (
                                            <span className="text-xs text-dark-dark-grey bg-light-orange border border-dark-orange rounded px-1.5 py-0.5">{p.error}</span>
                                        ) : (
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                                                p.listed
                                                    ? "bg-desaturated-green text-dark-green border-green"
                                                    : "bg-light-red text-dark-red border-mid-red"
                                            }`}>
                                                {p.listed ? "Listed" : "Not listed"}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {hasUnlisted && (
                        <div className="flex items-center justify-end pt-3 border-t border-light-light-grey">
                            {synced ? (
                                <span className="flex items-center gap-1.5 text-sm font-medium text-dark-green bg-desaturated-green border border-green rounded-md px-3 py-1.5">
                                    <CheckCircleIcon fontSize="small"/>
                                    Sync queued
                                </span>
                            ) : (
                                <button
                                    onClick={queueSync}
                                    disabled={syncing}
                                    className="flex items-center gap-1.5 text-sm font-medium text-blue border border-blue rounded-md px-3 py-1.5 hover:bg-light-blue disabled:text-light-grey disabled:border-light-light-grey disabled:bg-transparent disabled:cursor-not-allowed transition-colors"
                                >
                                    <SyncIcon fontSize="small" className={syncing ? "animate-spin" : ""}/>
                                    {syncing ? "Queuing…" : "Sync now"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </ModalV2>
    );
}
